'use client'

import { useMemo } from 'react'

import { useLocation } from '@marketplace-web/shared/browser'
import * as ConversationTypes from '@marketplace-web/domain/conversation-messaging-data'
import {
  ConversationMessageType,
  TransactionSide,
} from '@marketplace-web/domain/conversation-messaging-data'
import { useConversationContext } from '@marketplace-web/domain/core-conversation'
import { useSession } from '@marketplace-web/shared/session'
import { BrazeInboxMessage, useBrazeInboxMessageCards } from '@marketplace-web/shared/braze'

import ConversationMessage from './ConversationMessage'
import MessageTime from '../MessageTime'
import { isMessageGrouped, getConversationIdFromUrl } from '../../utils'

type Props = {
  toggleSidebar?: () => void
  openOfferModal: () => void
}

const ConversationMessages = ({ toggleSidebar, openOfferModal }: Props) => {
  const { relativeUrl } = useLocation()
  const conversationId = getConversationIdFromUrl(relativeUrl)
  const { conversation } = useConversationContext()
  const { user } = useSession()

  const messages = conversation?.messages
  const currentUserId = user?.id
  const oppositeUser = conversation?.oppositeUser

  const { inboxMessageCards } = useBrazeInboxMessageCards()
  const brazeConversationCard = useMemo(
    () => inboxMessageCards?.find(card => card.id === conversationId),
    [conversationId, inboxMessageCards],
  )

  const isCurrentAuthor = (message: ConversationTypes.ConversationMessage) => {
    if (message.entityType !== ConversationMessageType.Message) {
      return false
    }

    const entity = message.message as ConversationTypes.Message

    const isPeekingUser =
      entity.userId !== currentUserId?.toString() && oppositeUser?.id !== String(entity?.userId)

    if (isPeekingUser) {
      return String(entity?.userId) !== oppositeUser?.id
    }

    return entity?.userId === currentUserId?.toString()
  }

  if (brazeConversationCard) {
    return (
      <BrazeInboxMessage
        prefix={<MessageTime time={brazeConversationCard.updated.toISOString()} />}
        card={brazeConversationCard}
      />
    )
  }

  if (!messages) {
    return null
  }

  const userSide =
    conversation?.buyerId === currentUserId ? TransactionSide.Buyer : TransactionSide.Seller

  return (
    <>
      {messages.map((message, index) => (
        <ConversationMessage
          key={index}
          {...message}
          userSide={userSide}
          transactionId={conversation?.transactionId}
          isGrouped={isMessageGrouped(message, messages?.[index - 1])}
          isLast={index + 1 === messages.length}
          isCurrentAuthor={isCurrentAuthor(message)}
          toggleSidebar={toggleSidebar}
          openOfferModal={openOfferModal}
          conversationId={conversation?.id}
        />
      ))}
    </>
  )
}

export default ConversationMessages
